/**
 * Client for the Worker's /v1/corridor, used to warm a whole route ahead of a simulated drive.
 * The phone calls the corridor once a minute from wherever it is; here we hop along the track
 * every few kilometres and keep asking until nothing is pending. Runs in Node and the browser
 * (global fetch only), so the CLI and the web demo share it.
 */
import { bearingDeg, haversineM } from './geo';
import type { TrackPoint } from './gpx';
import type { Area, Feature, FeatureFixture, FeatureSighting, Landmark } from './types';

export interface CorridorRow {
  page_id: number;
  title: string;
  lat: number;
  lon: number;
  wiki_url: string | null;
  trigger_radius_m: number;
  /** Absent from Workers before 2026-09-22. */
  wiki_type?: string | null;
  script: string;
  headline: string | null;
  tone: string | null;
  est_seconds: number;
  updated_at: number;
  distance_m: number;
}

export interface CorridorResponse {
  server_time: number;
  prompt_version: string;
  tier: string;
  warming: boolean;
  pending: number;
  landmarks: CorridorRow[];
  /** Ready rundowns for the call's cell, most specific first; absent from older Workers. */
  areas?: Area[];
  /** Rundowns for the call's cell still to be looked up or written. */
  area_backlog?: number;
  /** Rivers, forests and wilderness with a script, shapes clipped round the call; absent from older Workers. */
  features?: Feature[];
  /** Feature cells ahead still to be looked up, and scripts near the car still to be written. */
  feature_backlog?: number;
}

/** A landmark as the app caches it, plus the tone the Worker reports (shown on review pages). */
export type WarmedLandmark = Landmark & { tone?: string | null };

export interface SamplePoint {
  lat: number;
  lon: number;
  heading: number | null;
}

export interface WarmProgress {
  pass: number;
  step: number;
  steps: number;
  ready: number;
  pending: number;
  warming: boolean;
}

export interface WarmOptions {
  baseUrl: string;
  deviceId: string;
  demoToken?: string;
  /** Distance between corridor calls along the track. */
  stepKm?: number;
  /** Lookahead passed to the Worker (it clamps to 5–40). */
  lookaheadKm?: number;
  /** Give up after this many full passes over the track. */
  maxPasses?: number;
  /** Wait between passes so background generation can land. */
  passDelayMs?: number;
  onProgress?: (p: WarmProgress) => void;
  /** Every response, with where it was asked from (the warm CLI records area sightings). */
  onCorridor?: (point: SamplePoint, r: CorridorResponse) => void;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  signal?: AbortSignal;
}

export class CorridorError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string | null,
  ) {
    super(message);
  }
}

/**
 * Collects the features a warm's corridor calls returned, with the shape each call sent, as the
 * phone would have held them (it replaces them on every sync). The last answer at each point wins:
 * later passes carry scripts written since. Shared by `npm run warm` and Route Lab.
 */
export function featureCollector(): { add(point: SamplePoint, r: CorridorResponse): void; fixture(): FeatureFixture | undefined } {
  const features = new Map<number, Feature>();
  const sightings = new Map<string, FeatureSighting>();
  return {
    add(point, r) {
      for (const f of r.features ?? []) features.set(f.page_id, f);
      sightings.set(`${point.lat},${point.lon}`, { lat: point.lat, lon: point.lon, features: (r.features ?? []).map((f) => ({ page_id: f.page_id, geom: f.geom })) });
    },
    fixture() {
      return features.size ? { features: [...features.values()], sightings: [...sightings.values()] } : undefined;
    },
  };
}

/** Points every `stepKm` along the track, each with the road's heading there. */
export function samplePoints(track: TrackPoint[], stepKm = 10): SamplePoint[] {
  if (!track.length) return [];
  const out: SamplePoint[] = [];
  const headingAt = (i: number): number | null => {
    // Look roughly 200 m ahead so a wiggle in the polyline does not swing the heading.
    let j = i + 1;
    while (j < track.length - 1 && haversineM(track[i]!.lat, track[i]!.lon, track[j]!.lat, track[j]!.lon) < 200) j++;
    const b = track[Math.min(j, track.length - 1)]!;
    if (b === track[i]) return null;
    return bearingDeg(track[i]!.lat, track[i]!.lon, b.lat, b.lon);
  };
  let acc = 0;
  let next = 0;
  for (let i = 0; i < track.length; i++) {
    if (i > 0) acc += haversineM(track[i - 1]!.lat, track[i - 1]!.lon, track[i]!.lat, track[i]!.lon);
    if (acc >= next) {
      out.push({ lat: track[i]!.lat, lon: track[i]!.lon, heading: headingAt(i) });
      next += stepKm * 1000;
    }
  }
  const last = track[track.length - 1]!;
  const tail = out[out.length - 1]!;
  if (haversineM(tail.lat, tail.lon, last.lat, last.lon) > 1000) out.push({ lat: last.lat, lon: last.lon, heading: headingAt(track.length - 2) });
  return out;
}

export function toLandmark(r: CorridorRow): WarmedLandmark {
  return {
    page_id: r.page_id,
    title: r.title,
    lat: r.lat,
    lon: r.lon,
    trigger_radius_m: r.trigger_radius_m,
    headline: r.headline,
    script: r.script,
    est_seconds: r.est_seconds,
    wiki_url: r.wiki_url,
    wiki_type: r.wiki_type ?? null,
    tone: r.tone,
  };
}

export async function fetchCorridor(
  point: SamplePoint,
  opts: Pick<WarmOptions, 'baseUrl' | 'deviceId' | 'demoToken' | 'lookaheadKm' | 'fetchImpl' | 'signal'>,
): Promise<CorridorResponse> {
  const qs = new URLSearchParams({ lat: String(point.lat), lon: String(point.lon), km: String(opts.lookaheadKm ?? 20) });
  if (point.heading != null) qs.set('heading', String(Math.round(point.heading)));
  const headers: Record<string, string> = { 'x-device-id': opts.deviceId };
  if (opts.demoToken) headers['x-demo-token'] = opts.demoToken;
  const f = opts.fetchImpl ?? fetch;
  const res = await f(`${opts.baseUrl.replace(/\/$/, '')}/v1/corridor?${qs}`, { headers, signal: opts.signal ?? null });
  if (!res.ok) {
    let code: string | null = null;
    try {
      code = ((await res.json()) as { error?: string }).error ?? null;
    } catch {
      /* not json */
    }
    throw new CorridorError(`corridor ${res.status}${code ? ` (${code})` : ''}`, res.status, code);
  }
  return (await res.json()) as CorridorResponse;
}

/**
 * Warm every corridor along the track and return the ready landmarks in route order. Stops when
 * a full pass finds nothing pending and no new landmark, or when the Worker is not warming and
 * has nothing pending (no API key, or the hourly budget is spent).
 */
export async function warmRoute(track: TrackPoint[], opts: WarmOptions): Promise<WarmedLandmark[]> {
  const points = samplePoints(track, opts.stepKm ?? 10);
  const sleep = opts.sleep ?? ((ms) => new Promise<void>((r) => setTimeout(r, ms)));
  const maxPasses = opts.maxPasses ?? 12;
  const ready = new Map<number, WarmedLandmark>();
  const areas = new Set<number>();
  let prevReady = -1;
  let prevAreas = -1;

  for (let pass = 1; pass <= maxPasses; pass++) {
    let pending = 0;
    let areaBacklog = 0;
    let warming = false;
    for (let i = 0; i < points.length; i++) {
      const r = await fetchCorridor(points[i]!, opts);
      for (const row of r.landmarks) if (!ready.has(row.page_id)) ready.set(row.page_id, toLandmark(row));
      for (const a of r.areas ?? []) areas.add(a.page_id);
      // Features are looked up and written one per call too, so they hold a pass open the same way.
      areaBacklog = Math.max(areaBacklog, r.area_backlog ?? 0, r.feature_backlog ?? 0);
      for (const f of r.features ?? []) areas.add(f.page_id);
      opts.onCorridor?.(points[i]!, r);
      // Corridors overlap, so a sum would count the same cooking row several times.
      pending = Math.max(pending, r.pending);
      warming ||= r.warming;
      opts.onProgress?.({ pass, step: i + 1, steps: points.length, ready: ready.size, pending, warming });
    }
    // Areas are written one per call, so a pass that added one is not settled yet either.
    const settled = pending === 0 && areaBacklog === 0 && ready.size === prevReady && areas.size === prevAreas;
    const stalled = pending === 0 && !warming;
    if (settled || stalled) break;
    prevReady = ready.size;
    prevAreas = areas.size;
    await sleep(opts.passDelayMs ?? 4000);
  }
  return [...ready.values()];
}
